import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  ArrowLeft, Users, FileQuestion, BarChart3, ClipboardList, Bell, Tag, Settings, Loader2, Languages, FileCheck, BrainCircuit, BookOpen, Database, Atom, AlertTriangle, RefreshCw
} from 'lucide-react';

// Import tab components
import {
  AdminUsersTab,
  AdminCouponsTab,
  AdminAnalyticsTab,
  AdminQuestionsTab,
  AdminResultsTab,
  AdminNotificationsTab,
  AdminTracksTab,
  AdminTranslateTab,
  AdminMockExamsTab,
  AdminQuestionEngineTab,
  SystemMaintenanceTab
} from '@/components/admin';
import AdminGlossaryTab from '@/components/admin/AdminGlossaryTab';
import AdminKnowledgeBase from '@/components/admin/AdminKnowledgeBase';
import AdminAKUTab from '@/components/admin/AdminAKUTab';
import QuickStartDialog, { shouldShowQuickStart } from '@/components/admin/QuickStartDialog';

const AdminPanel = () => {
  const navigate = useNavigate();
  const { getAuthHeaders, user } = useAuth();
  
  // Tab State - persist current tab
  const [activeTab, setActiveTab] = useState('users');
  
  // Quick Start Dialog state
  const [showQuickStart, setShowQuickStart] = useState(false);
  
  // Loading & Data States
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null); // Error banner state
  const [stats, setStats] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [users, setUsers] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [trackSettings, setTrackSettings] = useState([]);
  const [courses, setCourses] = useState([]);  // Course list for dropdowns
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsPeriod, setAnalyticsPeriod] = useState(30);
  const [notifications, setNotifications] = useState({ pending: [], recent_sent: [] });
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [sendingNotifications, setSendingNotifications] = useState(false);
  
  // Fetch guards to prevent duplicate requests
  const fetchGuard = useRef({ adminData: false, analytics: false, courses: false });
  const initialFetchDone = useRef(false);

  // Filter States
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [attemptFilterUser, setAttemptFilterUser] = useState('');
  const [attemptFilterTrack, setAttemptFilterTrack] = useState('');
  const [couponFilterTrack, setCouponFilterTrack] = useState('');
  const [couponFilterUsed, setCouponFilterUsed] = useState('');

  // Dialog States
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isCouponDialogOpen, setIsCouponDialogOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [couponLoading, setCouponLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // Form States
  const [newCoupon, setNewCoupon] = useState({
    track: 'PMP',
    quantity: 1,
    validity_months: 6,
    discount_percent: 100
  });

  const [newQuestion, setNewQuestion] = useState({
    question_text: '',
    question_text_de: '',
    question_text_es: '',
    question_text_fr: '',
    question_text_zh: '',
    question_text_ru: '',
    question_text_it: '',
    question_type: 'single',
    choices: [
      { id: 'A', text: '', text_de: '', text_es: '', text_fr: '', text_zh: '', text_ru: '', text_it: '' },
      { id: 'B', text: '', text_de: '', text_es: '', text_fr: '', text_zh: '', text_ru: '', text_it: '' },
      { id: 'C', text: '', text_de: '', text_es: '', text_fr: '', text_zh: '', text_ru: '', text_it: '' },
      { id: 'D', text: '', text_de: '', text_es: '', text_fr: '', text_zh: '', text_ru: '', text_it: '' }
    ],
    correct_answers: [],
    explanation: '',
    explanation_de: '',
    explanation_es: '',
    explanation_fr: '',
    explanation_zh: '',
    explanation_ru: '',
    explanation_it: '',
    category: 'PMP',
    difficulty: 'medium',
    cognitive_level: null
  });

  // Dynamic categories from courses - always include defaults plus any from courses
  // Default categories fallback (used when courses haven't loaded yet)
  const DEFAULT_CATEGORIES = ['PSM', 'PSPO', 'SAFe', 'ITIL', 'PRINCE2'];
  const categories = courses.length > 0 
    ? courses.map(c => c.code)
    : DEFAULT_CATEGORIES;

  // ==================== DATA FETCHING ====================
  // Stable fetch functions with useCallback
  const fetchAdminData = useCallback(async () => {
    // Prevent duplicate requests
    if (fetchGuard.current.adminData) {
      return;
    }
    fetchGuard.current.adminData = true;
    
    try {
      setLoading(true);
      setLoadError(null);
      const headers = getAuthHeaders();
      
      // Fetch all data in parallel, but handle each response individually
      const results = await Promise.allSettled([
        fetch(`${API}/admin/stats`, { headers }),
        fetch(`${API}/questions`, { headers }),
        fetch(`${API}/admin/users`, { headers }),
        fetch(`${API}/admin/attempts`, { headers }),
        fetch(`${API}/admin/coupons`, { headers }),
        fetch(`${API}/admin/tracks`, { headers })
      ]);

      const [statsRes, questionsRes, usersRes, attemptsRes, couponsRes, tracksRes] = results;
      
      let successCount = 0;
      const errors = [];

      // Process each response - use fulfilled results, ignore rejected
      // Wrap JSON parsing in try-catch to handle malformed responses
      try {
        if (statsRes.status === 'fulfilled' && statsRes.value.ok) {
          setStats(await statsRes.value.json());
          successCount++;
        } else if (statsRes.status === 'fulfilled' && statsRes.value.status === 401) {
          errors.push('stats:401');
        }
      } catch (e) { /* ignore parse errors */ }
      
      try {
        if (questionsRes.status === 'fulfilled' && questionsRes.value.ok) {
          setQuestions(await questionsRes.value.json());
          successCount++;
        }
      } catch (e) { /* ignore parse errors */ }
      
      try {
        if (usersRes.status === 'fulfilled' && usersRes.value.ok) {
          const usersData = await usersRes.value.json();
          setUsers(usersData?.users || []);
          successCount++;
        }
      } catch (e) { /* ignore parse errors */ }
      
      try {
        if (attemptsRes.status === 'fulfilled' && attemptsRes.value.ok) {
          const attemptsData = await attemptsRes.value.json();
          setAttempts(attemptsData?.attempts || []);
          successCount++;
        }
      } catch (e) { /* ignore parse errors */ }
      
      try {
        if (couponsRes.status === 'fulfilled' && couponsRes.value.ok) {
          const couponsData = await couponsRes.value.json();
          setCoupons(couponsData?.coupons || []);
          successCount++;
        }
      } catch (e) { /* ignore parse errors */ }
      
      try {
        if (tracksRes.status === 'fulfilled' && tracksRes.value.ok) {
          const tracksData = await tracksRes.value.json();
          setTrackSettings(tracksData?.tracks || []);
          successCount++;
        }
      } catch (e) { /* ignore parse errors */ }

      // Fetch courses separately (from course-migration endpoint)
      try {
        const courseRes = await fetch(`${API}/admin/course-migration/status`, { headers });
        if (courseRes.ok) {
          const courseData = await courseRes.json();
          setCourses(courseData?.status?.courses?.list || []);
          successCount++;
        }
      } catch (e) { /* ignore parse errors */ }

      // Show error banner if critical requests failed
      if (errors.includes('stats:401')) {
        setLoadError({ code: '401', message: 'Sitzung abgelaufen. Bitte erneut anmelden.' });
      } else if (successCount === 0) {
        setLoadError({ code: 'LOAD_FAILED', message: 'Admin-Daten konnten nicht geladen werden.' });
      } else if (successCount < 4) {
        // Partial failure - show warning but continue
      }
    } catch (error) {
      console.error('Admin data fetch error:', error);
      setLoadError({ code: 'NETWORK', message: `Netzwerkfehler: ${error.message}` });
    } finally {
      setLoading(false);
      fetchGuard.current.adminData = false;
    }
  }, [getAuthHeaders]);

  const fetchAnalytics = useCallback(async () => {
    if (fetchGuard.current.analytics) return;
    fetchGuard.current.analytics = true;
    
    try {
      setAnalyticsLoading(true);
      const headers = getAuthHeaders();
      const res = await fetch(`${API}/admin/analytics?period_days=${analyticsPeriod}`, { headers });
      if (res.ok) setAnalytics(await res.json());
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setAnalyticsLoading(false);
      fetchGuard.current.analytics = false;
    }
  }, [getAuthHeaders, analyticsPeriod]);

  const fetchCourses = useCallback(async () => {
    if (fetchGuard.current.courses) return;
    fetchGuard.current.courses = true;
    
    try {
      const headers = getAuthHeaders();
      // Use course-migration/status endpoint for course list
      const res = await fetch(`${API}/admin/course-migration/status`, { headers });
      if (res.ok) {
        const data = await res.json();
        const courseList = data?.status?.courses?.list || [];
        setCourses(courseList);
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
    } finally {
      fetchGuard.current.courses = false;
    }
  }, [getAuthHeaders]);
  
  // Initial data load - only once when user is available
  useEffect(() => {
    if (!user || initialFetchDone.current) return;
    
    initialFetchDone.current = true;
    
    fetchAdminData();
    fetchAnalytics();
    
    // Show Quick Start dialog on first visit - ONLY for Admin users
    if (shouldShowQuickStart() && user?.role === 'admin') {
      // Small delay to let the page load first
      setTimeout(() => setShowQuickStart(true), 500);
    }
  }, [user, fetchAdminData, fetchAnalytics]);

  // Analytics period change handler - separate effect
  useEffect(() => {
    if (!initialFetchDone.current) return; // Skip if initial fetch not done
    fetchAnalytics();
  }, [analyticsPeriod]); // Only re-fetch when period changes

  // Only refresh questions data (without full page reload/loading state)
  const refreshQuestions = async () => {
    try {
      const headers = getAuthHeaders();
      const [statsRes, questionsRes] = await Promise.all([
        fetch(`${API}/admin/stats`, { headers }),
        fetch(`${API}/questions`, { headers })
      ]);
      
      if (statsRes.ok) setStats(await statsRes.json());
      if (questionsRes.ok) setQuestions(await questionsRes.json());
    } catch (error) {
      console.error('Error refreshing questions:', error);
    }
  };

  const fetchNotifications = async () => {
    try {
      setNotificationsLoading(true);
      const headers = getAuthHeaders();
      const res = await fetch(`${API}/admin/notifications`, { headers });
      if (res.ok) setNotifications(await res.json());
    } catch (error) {
      toast.error('Error loading notifications');
    } finally {
      setNotificationsLoading(false);
    }
  };

  // ==================== COURSE HANDLERS ====================
  const handleCreateCourse = async (courseData) => {
    try {
      const res = await fetch(`${API}/admin/courses`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(courseData)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Course could not be created');
      toast.success(`Course "${courseData.name}" created successfully`);
      fetchCourses();
      fetchAdminData();
      return true;
    } catch (error) {
      toast.error(error.message);
      return false;
    }
  };

  const handleUpdateCourse = async (courseCode, courseData) => {
    try {
      const res = await fetch(`${API}/admin/courses/${courseCode}`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(courseData)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Course could not be updated');
      toast.success(`Course "${courseCode}" updated successfully`);
      fetchCourses();
      return true;
    } catch (error) {
      toast.error(error.message);
      return false;
    }
  };

  const handleDeleteCourse = async (courseCode) => {
    try {
      const res = await fetch(`${API}/admin/courses/${courseCode}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Course could not be deleted');
      toast.success(`Course "${courseCode}" deleted successfully`);
      fetchCourses();
      return true;
    } catch (error) {
      toast.error(error.message);
      return false;
    }
  };

  // ==================== QUESTION HANDLERS ====================
  // Helper function to extract error message from API responses
  const extractErrorMessage = (detail, fallback = 'Ein Fehler ist aufgetreten') => {
    if (!detail) return fallback;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      // Pydantic validation errors are arrays of objects
      return detail.map(err => {
        if (typeof err === 'string') return err;
        const field = err.loc?.slice(-1)[0] || 'Feld';
        const msg = err.msg || 'Ungültig';
        return `${field}: ${msg}`;
      }).join(', ');
    }
    if (typeof detail === 'object') {
      return detail.msg || detail.message || JSON.stringify(detail);
    }
    return fallback;
  };

  const handleCreateQuestion = async () => {
    try {
      // Frontend validation
      if (!newQuestion.question_text?.trim()) {
        toast.error('Bitte geben Sie den Fragentext ein');
        return;
      }
      
      // Validate correct_answers based on question type
      const correctAnswers = newQuestion.correct_answers || [];
      if (newQuestion.question_type === 'truefalse') {
        if (!correctAnswers.length || !['true', 'false'].includes(correctAnswers[0])) {
          toast.error('Bitte wählen Sie True oder False als korrekte Antwort');
          return;
        }
      } else if (newQuestion.question_type === 'fill') {
        if (!correctAnswers.length || !correctAnswers.some(a => a?.trim())) {
          toast.error('Bitte geben Sie mindestens eine korrekte Antwort ein');
          return;
        }
      } else if (newQuestion.question_type === 'match') {
        // Match questions don't need correct_answers validation here
      } else {
        // Single or Multi choice
        if (!correctAnswers.length) {
          toast.error('Bitte markieren Sie mindestens eine korrekte Antwort');
          return;
        }
      }
      
      // Filter out empty choices
      const filteredChoices = newQuestion.choices?.filter(c => c.text?.trim()) || [];
      
      const questionData = {
        ...newQuestion,
        choices: filteredChoices.length > 0 ? filteredChoices : undefined
      };
      
      
      const res = await fetch(`${API}/questions`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(questionData)
      });
      
      const responseData = await res.json();
      
      if (!res.ok) {
        const errorMsg = extractErrorMessage(responseData.detail, 'Frage konnte nicht erstellt werden');
        throw new Error(errorMsg);
      }
      
      toast.success('Frage erfolgreich erstellt');
      setIsAddDialogOpen(false);
      resetNewQuestion();
      refreshQuestions(); // Use refreshQuestions instead of fetchAdminData to stay on tab
    } catch (error) {
      console.error('Create question error:', error);
      toast.error(error.message);
    }
  };

  const handleUpdateQuestion = async () => {
    if (!editingQuestion) return;
    try {
      const res = await fetch(`${API}/questions/${editingQuestion.question_id}`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(editingQuestion)
      });
      if (!res.ok) {
        const responseData = await res.json().catch(() => ({}));
        const errorMsg = extractErrorMessage(responseData.detail, 'Frage konnte nicht aktualisiert werden');
        throw new Error(errorMsg);
      }
      toast.success('Frage erfolgreich aktualisiert');
      setIsEditDialogOpen(false);
      setEditingQuestion(null);
      refreshQuestions(); // Use refreshQuestions instead of fetchAdminData to stay on tab
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!confirm('Möchten Sie diese Frage wirklich löschen?')) return;
    try {
      const res = await fetch(`${API}/questions/${questionId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (!res.ok) throw new Error('Question could not be deleted');
      toast.success('Frage gelöscht');
      refreshQuestions(); // Use refreshQuestions instead of fetchAdminData to stay on tab
    } catch (error) {
      toast.error(error.message);
    }
  };

  const resetNewQuestion = () => {
    setNewQuestion({
      question_text: '',
      question_text_de: '',
      question_text_es: '',
      question_text_fr: '',
      question_text_zh: '',
      question_text_ru: '',
      question_text_it: '',
      question_type: 'single',
      choices: [
        { id: 'A', text: '', text_de: '', text_es: '', text_fr: '', text_zh: '', text_ru: '', text_it: '' },
        { id: 'B', text: '', text_de: '', text_es: '', text_fr: '', text_zh: '', text_ru: '', text_it: '' },
        { id: 'C', text: '', text_de: '', text_es: '', text_fr: '', text_zh: '', text_ru: '', text_it: '' },
        { id: 'D', text: '', text_de: '', text_es: '', text_fr: '', text_zh: '', text_ru: '', text_it: '' }
      ],
      correct_answers: [],
      explanation: '',
      explanation_de: '',
      explanation_es: '',
      explanation_fr: '',
      explanation_zh: '',
      explanation_ru: '',
      explanation_it: '',
      category: 'PMP',
      difficulty: 'medium'
    });
  };

  // ==================== IMPORT/EXPORT HANDLERS ====================
  const handleImportCSV = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setImportLoading(true);
    setImportResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await fetch(`${API}/questions/import`, {
        method: 'POST',
        headers: { Authorization: getAuthHeaders().Authorization },
        body: formData
      });
      
      const result = await res.json();
      if (!res.ok) throw new Error(result.detail || 'Import failed');
      
      setImportResult(result);
      fetchAdminData();
    } catch (error) {
      setImportResult({ error: error.message });
    } finally {
      setImportLoading(false);
      event.target.value = '';
    }
  };

  const handleExportCSV = async (category = null) => {
    setExportLoading(true);
    try {
      const url = category 
        ? `${API}/questions/export?category=${category}`
        : `${API}/questions/export`;
      
      const res = await fetch(url, { headers: getAuthHeaders() });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Export failed');
      }
      
      const blob = await res.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `questions_export_${category || 'all'}_${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      toast.success('Export downloaded successfully');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setExportLoading(false);
    }
  };

  // ==================== USER HANDLERS ====================
  const handleDeleteUser = async (userId) => {
    try {
      const res = await fetch(`${API}/admin/users/${userId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'User could not be deleted');
      }
      toast.success('User deleted successfully');
      fetchAdminData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // ==================== COUPON HANDLERS ====================
  const handleCreateCoupon = async () => {
    setCouponLoading(true);
    try {
      const res = await fetch(`${API}/admin/coupons`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(newCoupon)
      });
      const result = await res.json();
      if (!res.ok) throw new Error('Coupon could not be created');
      
      setCoupons([...coupons, ...result.coupons]);
      toast.success(`${result.count} coupon(s) created`);
      setIsCouponDialogOpen(false);
      setNewCoupon({ track: 'PMP', quantity: 1, validity_months: 6, discount_percent: 100 });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setCouponLoading(false);
    }
  };

  const handleDeleteCoupon = async (couponId) => {
    if (!confirm('Are you sure you want to delete this coupon?')) return;
    try {
      const res = await fetch(`${API}/admin/coupons/${couponId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (!res.ok) throw new Error('Coupon could not be deleted');
      setCoupons(coupons.filter(c => c.coupon_id !== couponId));
      toast.success('Coupon deleted');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  // ==================== NOTIFICATION HANDLERS ====================
  const handleSendNotifications = async () => {
    setSendingNotifications(true);
    try {
      const res = await fetch(`${API}/admin/notifications/send`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Error sending');
      toast.success(`${data.sent_count} notification(s) sent`);
      fetchNotifications();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSendingNotifications(false);
    }
  };

  // ==================== TRACK HANDLERS ====================
  const handleTrackToggle = async (track, isAvailable) => {
    try {
      
      // Use the courses endpoint to update is_active status
      const res = await fetch(`${API}/admin/courses/${track}`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ is_active: isAvailable })
      });
      
      let data;
      try {
        const text = await res.text();
        data = text ? JSON.parse(text) : {};
      } catch (e) {
        data = {};
      }
      
      
      if (!res.ok) throw new Error(data.detail || 'Setting could not be saved');
      
      // Update local state
      setCourses(courses.map(c => 
        c.code === track ? { ...c, is_active: isAvailable } : c
      ));
      
      // Also update track settings for consistency
      setTrackSettings(trackSettings.map(t => 
        t.track === track ? { ...t, is_available: isAvailable } : t
      ));
      
      toast.success(`${track} ${isAvailable ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Track toggle error:', error);
      toast.error(error.message);
    }
  };

  // ==================== UTILITY FUNCTIONS ====================
  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatTime = (seconds) => {
    if (!seconds) return '—';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };

  // ==================== LOADING STATE ====================
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#0047AB]" />
      </div>
    );
  }

  // ==================== RENDER ====================
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 p-4 md:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Error Banner */}
        {loadError && (
          <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center justify-between" data-testid="error-banner">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              <div>
                <p className="text-sm font-medium text-red-800 dark:text-red-200">
                  Fehler [{loadError.code}]
                </p>
                <p className="text-sm text-red-600 dark:text-red-400">
                  {loadError.message}
                </p>
              </div>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => {
                setLoadError(null);
                initialFetchDone.current = false;
                fetchAdminData();
              }}
              className="border-red-300 text-red-700 hover:bg-red-100 dark:border-red-700 dark:text-red-300 dark:hover:bg-red-900/30"
              data-testid="retry-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Erneut versuchen
            </Button>
          </div>
        )}
        
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/dashboard')} data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <h1 className="text-2xl font-bold text-slate-800 dark:text-white">Admin Panel</h1>
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Logged in as {user?.email}
          </p>
        </div>

        {/* Main Tool Navigation - Large Cards */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {/* Row 1: Main Tools */}
            <button
              onClick={() => setActiveTab('users')}
              className={`p-4 rounded-xl border-2 transition-all hover:scale-[1.02] ${
                activeTab === 'users' 
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/30 shadow-lg' 
                  : 'border-slate-200 dark:border-slate-700 hover:border-blue-300'
              }`}
              data-testid="nav-users"
            >
              <Users className={`w-8 h-8 mx-auto mb-2 ${activeTab === 'users' ? 'text-blue-600' : 'text-slate-500'}`} />
              <span className={`text-sm font-medium ${activeTab === 'users' ? 'text-blue-700 dark:text-blue-300' : 'text-slate-600 dark:text-slate-400'}`}>
                Participants
              </span>
            </button>

            <button
              onClick={() => setActiveTab('question-engine')}
              className={`p-4 rounded-xl border-2 transition-all hover:scale-[1.02] ${
                activeTab === 'question-engine' 
                  ? 'border-purple-500 bg-gradient-to-br from-purple-50 to-blue-50 dark:from-purple-950/30 dark:to-blue-950/30 shadow-lg' 
                  : 'border-purple-200 dark:border-purple-800 bg-gradient-to-br from-purple-50/50 to-blue-50/50 dark:from-purple-950/10 dark:to-blue-950/10 hover:border-purple-400'
              }`}
              data-testid="nav-ai-engine"
            >
              <BrainCircuit className={`w-8 h-8 mx-auto mb-2 ${activeTab === 'question-engine' ? 'text-purple-600' : 'text-purple-500'}`} />
              <span className={`text-sm font-semibold ${activeTab === 'question-engine' ? 'text-purple-700 dark:text-purple-300' : 'text-purple-600 dark:text-purple-400'}`}>
                AI Engine
              </span>
            </button>

            <button
              onClick={() => setActiveTab('results')}
              className={`p-4 rounded-xl border-2 transition-all hover:scale-[1.02] ${
                activeTab === 'results' 
                  ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30 shadow-lg' 
                  : 'border-slate-200 dark:border-slate-700 hover:border-emerald-300'
              }`}
              data-testid="nav-results"
            >
              <ClipboardList className={`w-8 h-8 mx-auto mb-2 ${activeTab === 'results' ? 'text-emerald-600' : 'text-slate-500'}`} />
              <span className={`text-sm font-medium ${activeTab === 'results' ? 'text-emerald-700 dark:text-emerald-300' : 'text-slate-600 dark:text-slate-400'}`}>
                Results
              </span>
            </button>

            <button
              onClick={() => setActiveTab('questions')}
              className={`p-4 rounded-xl border-2 transition-all hover:scale-[1.02] ${
                activeTab === 'questions' 
                  ? 'border-orange-500 bg-orange-50 dark:bg-orange-950/30 shadow-lg' 
                  : 'border-slate-200 dark:border-slate-700 hover:border-orange-300'
              }`}
              data-testid="nav-questions"
            >
              <FileQuestion className={`w-8 h-8 mx-auto mb-2 ${activeTab === 'questions' ? 'text-orange-600' : 'text-slate-500'}`} />
              <span className={`text-sm font-medium ${activeTab === 'questions' ? 'text-orange-700 dark:text-orange-300' : 'text-slate-600 dark:text-slate-400'}`}>
                Questions
              </span>
            </button>

            <button
              onClick={() => setActiveTab('mock-exams')}
              className={`p-4 rounded-xl border-2 transition-all hover:scale-[1.02] ${
                activeTab === 'mock-exams' 
                  ? 'border-cyan-500 bg-cyan-50 dark:bg-cyan-950/30 shadow-lg' 
                  : 'border-slate-200 dark:border-slate-700 hover:border-cyan-300'
              }`}
              data-testid="nav-mock-exams"
            >
              <FileCheck className={`w-8 h-8 mx-auto mb-2 ${activeTab === 'mock-exams' ? 'text-cyan-600' : 'text-slate-500'}`} />
              <span className={`text-sm font-medium ${activeTab === 'mock-exams' ? 'text-cyan-700 dark:text-cyan-300' : 'text-slate-600 dark:text-slate-400'}`}>
                Mock Exams
              </span>
            </button>

            {/* Row 2: Secondary Tools */}
            <button
              onClick={() => setActiveTab('translate')}
              className={`p-4 rounded-xl border-2 transition-all hover:scale-[1.02] ${
                activeTab === 'translate' 
                  ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-950/30 shadow-lg' 
                  : 'border-slate-200 dark:border-slate-700 hover:border-indigo-300'
              }`}
              data-testid="nav-translate"
            >
              <Languages className={`w-8 h-8 mx-auto mb-2 ${activeTab === 'translate' ? 'text-indigo-600' : 'text-slate-500'}`} />
              <span className={`text-sm font-medium ${activeTab === 'translate' ? 'text-indigo-700 dark:text-indigo-300' : 'text-slate-600 dark:text-slate-400'}`}>
                Translate
              </span>
            </button>

            <button
              onClick={() => setActiveTab('analytics')}
              className={`p-4 rounded-xl border-2 transition-all hover:scale-[1.02] ${
                activeTab === 'analytics' 
                  ? 'border-pink-500 bg-pink-50 dark:bg-pink-950/30 shadow-lg' 
                  : 'border-slate-200 dark:border-slate-700 hover:border-pink-300'
              }`}
              data-testid="nav-analytics"
            >
              <BarChart3 className={`w-8 h-8 mx-auto mb-2 ${activeTab === 'analytics' ? 'text-pink-600' : 'text-slate-500'}`} />
              <span className={`text-sm font-medium ${activeTab === 'analytics' ? 'text-pink-700 dark:text-pink-300' : 'text-slate-600 dark:text-slate-400'}`}>
                Analytics
              </span>
            </button>

            <button
              onClick={() => setActiveTab('notifications')}
              className={`p-4 rounded-xl border-2 transition-all hover:scale-[1.02] ${
                activeTab === 'notifications' 
                  ? 'border-amber-500 bg-amber-50 dark:bg-amber-950/30 shadow-lg' 
                  : 'border-slate-200 dark:border-slate-700 hover:border-amber-300'
              }`}
              data-testid="nav-notifications"
            >
              <Bell className={`w-8 h-8 mx-auto mb-2 ${activeTab === 'notifications' ? 'text-amber-600' : 'text-slate-500'}`} />
              <span className={`text-sm font-medium ${activeTab === 'notifications' ? 'text-amber-700 dark:text-amber-300' : 'text-slate-600 dark:text-slate-400'}`}>
                E-Mails
              </span>
            </button>

            <button
              onClick={() => setActiveTab('coupons')}
              className={`p-4 rounded-xl border-2 transition-all hover:scale-[1.02] ${
                activeTab === 'coupons' 
                  ? 'border-rose-500 bg-rose-50 dark:bg-rose-950/30 shadow-lg' 
                  : 'border-slate-200 dark:border-slate-700 hover:border-rose-300'
              }`}
              data-testid="nav-coupons"
            >
              <Tag className={`w-8 h-8 mx-auto mb-2 ${activeTab === 'coupons' ? 'text-rose-600' : 'text-slate-500'}`} />
              <span className={`text-sm font-medium ${activeTab === 'coupons' ? 'text-rose-700 dark:text-rose-300' : 'text-slate-600 dark:text-slate-400'}`}>
                Coupons
              </span>
            </button>

            <button
              onClick={() => setActiveTab('tracks')}
              className={`p-4 rounded-xl border-2 transition-all hover:scale-[1.02] ${
                activeTab === 'tracks' 
                  ? 'border-slate-500 bg-slate-100 dark:bg-slate-800 shadow-lg' 
                  : 'border-slate-200 dark:border-slate-700 hover:border-slate-400'
              }`}
              data-testid="nav-tracks"
            >
              <Settings className={`w-8 h-8 mx-auto mb-2 ${activeTab === 'tracks' ? 'text-slate-700 dark:text-slate-300' : 'text-slate-500'}`} />
              <span className={`text-sm font-medium ${activeTab === 'tracks' ? 'text-slate-800 dark:text-slate-200' : 'text-slate-600 dark:text-slate-400'}`}>
                Courses
              </span>
            </button>

            <button
              onClick={() => setActiveTab('glossary')}
              className={`p-4 rounded-xl border-2 transition-all hover:scale-[1.02] ${
                activeTab === 'glossary' 
                  ? 'border-teal-500 bg-teal-50 dark:bg-teal-950/30 shadow-lg' 
                  : 'border-slate-200 dark:border-slate-700 hover:border-teal-300'
              }`}
              data-testid="nav-glossary"
            >
              <BookOpen className={`w-8 h-8 mx-auto mb-2 ${activeTab === 'glossary' ? 'text-teal-600' : 'text-slate-500'}`} />
              <span className={`text-sm font-medium ${activeTab === 'glossary' ? 'text-teal-700 dark:text-teal-300' : 'text-slate-600 dark:text-slate-400'}`}>
                Glossar
              </span>
            </button>

            <button
              onClick={() => setActiveTab('maintenance')}
              className={`p-4 rounded-xl border-2 transition-all hover:scale-[1.02] ${
                activeTab === 'maintenance' 
                  ? 'border-orange-500 bg-orange-50 dark:bg-orange-950/30 shadow-lg' 
                  : 'border-slate-200 dark:border-slate-700 hover:border-orange-300'
              }`}
              data-testid="nav-maintenance"
            >
              <Database className={`w-8 h-8 mx-auto mb-2 ${activeTab === 'maintenance' ? 'text-orange-600' : 'text-slate-500'}`} />
              <span className={`text-sm font-medium ${activeTab === 'maintenance' ? 'text-orange-700 dark:text-orange-300' : 'text-slate-600 dark:text-slate-400'}`}>
                Wartung
              </span>
            </button>
          </div>

          {/* Users Tab - CONDITIONAL RENDERING with key for forced remount */}
          <TabsContent value="users">
            {activeTab === 'users' && (
              <AdminUsersTab
                key="users-tab"
                users={users}
                searchTerm={userSearchTerm}
                setSearchTerm={setUserSearchTerm}
                formatDate={formatDate}
                handleDeleteUser={handleDeleteUser}
              />
            )}
          </TabsContent>

          {/* Results Tab */}
          <TabsContent value="results">
            {activeTab === 'results' && (
              <AdminResultsTab
                key="results-tab"
                attempts={attempts}
                users={users}
                categories={categories}
                attemptFilterUser={attemptFilterUser}
                setAttemptFilterUser={setAttemptFilterUser}
                attemptFilterTrack={attemptFilterTrack}
                setAttemptFilterTrack={setAttemptFilterTrack}
                formatDate={formatDate}
                formatTime={formatTime}
              />
            )}
          </TabsContent>

          {/* Questions Tab */}
          <TabsContent value="questions">
            {activeTab === 'questions' && (
              <AdminQuestionsTab
                key="questions-tab"
                questions={questions}
                stats={stats}
                categories={categories}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                filterCategory={filterCategory}
                setFilterCategory={setFilterCategory}
                isAddDialogOpen={isAddDialogOpen}
                setIsAddDialogOpen={setIsAddDialogOpen}
                isEditDialogOpen={isEditDialogOpen}
                setIsEditDialogOpen={setIsEditDialogOpen}
                newQuestion={newQuestion}
                setNewQuestion={setNewQuestion}
                editingQuestion={editingQuestion}
              setEditingQuestion={setEditingQuestion}
              importLoading={importLoading}
              exportLoading={exportLoading}
              importResult={importResult}
              setImportResult={setImportResult}
              handleCreateQuestion={handleCreateQuestion}
              handleUpdateQuestion={handleUpdateQuestion}
              handleDeleteQuestion={handleDeleteQuestion}
              handleImportCSV={handleImportCSV}
              handleExportCSV={handleExportCSV}
              resetNewQuestion={resetNewQuestion}
              getAuthHeaders={getAuthHeaders}
              refreshQuestions={refreshQuestions}
              />
            )}
          </TabsContent>

          {/* Mock Exams Tab */}
          <TabsContent value="mock-exams">
            {activeTab === 'mock-exams' && (
              <AdminMockExamsTab key="mock-exams-tab" getAuthHeaders={getAuthHeaders} />
            )}
          </TabsContent>

          {/* Translate Tab */}
          <TabsContent value="translate">
            {activeTab === 'translate' && (
              <AdminTranslateTab key="translate-tab" getAuthHeaders={getAuthHeaders} />
            )}
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            {activeTab === 'analytics' && (
              <AdminAnalyticsTab
                key="analytics-tab"
                analytics={analytics}
                analyticsLoading={analyticsLoading}
                analyticsPeriod={analyticsPeriod}
                setAnalyticsPeriod={setAnalyticsPeriod}
              />
            )}
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications">
            {activeTab === 'notifications' && (
              <AdminNotificationsTab
                key="notifications-tab"
                notifications={notifications}
                notificationsLoading={notificationsLoading}
                sendingNotifications={sendingNotifications}
                fetchNotifications={fetchNotifications}
                handleSendNotifications={handleSendNotifications}
              />
            )}
          </TabsContent>

          {/* Coupons Tab */}
          <TabsContent value="coupons">
            {activeTab === 'coupons' && (
              <AdminCouponsTab
                key="coupons-tab"
                coupons={coupons}
                categories={categories}
                filterTrack={couponFilterTrack}
                setFilterTrack={setCouponFilterTrack}
                filterUsed={couponFilterUsed}
                setFilterUsed={setCouponFilterUsed}
                isCouponDialogOpen={isCouponDialogOpen}
                setIsCouponDialogOpen={setIsCouponDialogOpen}
                newCoupon={newCoupon}
                setNewCoupon={setNewCoupon}
                couponLoading={couponLoading}
                handleCreateCoupon={handleCreateCoupon}
                handleDeleteCoupon={handleDeleteCoupon}
                copyToClipboard={copyToClipboard}
                formatDate={formatDate}
              />
            )}
          </TabsContent>

          {/* Tracks Tab */}
          <TabsContent value="tracks">
            {activeTab === 'tracks' && (
              <AdminTracksTab
                key="tracks-tab"
                courses={courses}
                coursesLoading={loading}
                handleCreateCourse={handleCreateCourse}
                handleUpdateCourse={handleUpdateCourse}
                handleDeleteCourse={handleDeleteCourse}
                handleTrackToggle={handleTrackToggle}
                fetchCourses={fetchCourses}
              />
            )}
          </TabsContent>

          {/* Question Engine Tab - CRITICAL: Must be conditionally rendered */}
          <TabsContent value="question-engine">
            {activeTab === 'question-engine' && (
              <AdminQuestionEngineTab key={`ai-engine-${activeTab}`} getAuthHeaders={getAuthHeaders} />
            )}
          </TabsContent>

          {/* Glossary Tab */}
          <TabsContent value="glossary">
            {activeTab === 'glossary' && (
              <AdminGlossaryTab key="glossary-tab" getAuthHeaders={getAuthHeaders} />
            )}
          </TabsContent>
          
          {/* System Maintenance Tab (replaces Canonical ID Migration) */}
          <TabsContent value="maintenance">
            {activeTab === 'maintenance' && (
              <SystemMaintenanceTab key="maintenance-tab" getAuthHeaders={getAuthHeaders} />
            )}
          </TabsContent>
        </Tabs>

        {/* Stats Cards - Moved to bottom, smaller */}
        <div className="mt-8 pt-6 border-t border-slate-200 dark:border-slate-700">
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-3">Overview Statistics</p>
          <div className="grid grid-cols-3 gap-3">
            <div className="flex items-center gap-3 p-3 bg-slate-100 dark:bg-slate-800 rounded-lg">
              <Users className="w-5 h-5 text-blue-500" />
              <div>
                <p className="text-xs text-slate-500 dark:text-slate-400">Users</p>
                <p className="text-lg font-semibold text-slate-700 dark:text-slate-200">{stats?.total_users || 0}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-slate-100 dark:bg-slate-800 rounded-lg">
              <FileQuestion className="w-5 h-5 text-purple-500" />
              <div>
                <p className="text-xs text-slate-500 dark:text-slate-400">Questions</p>
                <p className="text-lg font-semibold text-slate-700 dark:text-slate-200">{stats?.total_questions || 0}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-slate-100 dark:bg-slate-800 rounded-lg">
              <BarChart3 className="w-5 h-5 text-emerald-500" />
              <div>
                <p className="text-xs text-slate-500 dark:text-slate-400">Tests</p>
                <p className="text-lg font-semibold text-slate-700 dark:text-slate-200">{stats?.total_attempts || 0}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Quick Start Dialog */}
      <QuickStartDialog
        isOpen={showQuickStart}
        onClose={() => setShowQuickStart(false)}
        onStartGuide={() => {
          setShowQuickStart(false);
          setActiveTab('question-engine');
          toast.success('Willkommen im Guided Mode!');
        }}
        userName={user?.full_name || user?.email?.split('@')[0] || 'Admin'}
      />
    </div>
  );
};

export default AdminPanel;
